import { useState, useEffect, useCallback } from "react";
import { debounce } from "./helper";
import { extractTextFromElement } from "./helper";
import useElementCrossing from "@hooks/useElementCrossing";

const pushToDataLayer = (
  event,
  eventAction,
  eventLabel,
  additionalData = {},
) => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      eventAction,
      eventLabel,
      ...additionalData,
    });
  }
};

const GTM = {
  pushToDataLayer,

  init: () => {
    GTM.scrollTracking();
    window.addEventListener("scroll", GTM.scrollTracking);
  },

  /*** Scroll tracking START ***/

  distanceScrolled: -1,
  scrollThresholds: [
    { percentage: 25, triggered: false },
    { percentage: 50, triggered: false },
    { percentage: 75, triggered: false },
    { percentage: 90, triggered: false },
    { percentage: 100, triggered: false },
  ],

  scrollTracking: debounce(() => {
    const scrollPosition = window.scrollY;
    const scrollPercentage = Math.round(
      (scrollPosition /
        (document.documentElement.scrollHeight - window.innerHeight)) *
        100,
    );

    GTM.distanceScrolled = scrollPosition;

    GTM.scrollThresholds.forEach((threshold) => {
      if (scrollPercentage >= threshold.percentage && !threshold.triggered) {
        threshold.triggered = true;
        pushToDataLayer("custom_scroll_depth", `${threshold.percentage}%`);
      }
    });
  }, 100),

  /*** Scroll tracking END ***/

  /*** Form tags START ***/

  formSubmission: (formName, completedFields) => {
    pushToDataLayer("form_submissions", formName);
    if (completedFields) {
      Object.keys(completedFields).forEach((field) => {
        const label = completedFields[field]
          ? `${field}_${completedFields[field]}`
          : field;
        pushToDataLayer("field_completion_submitted", formName, label);
      });
    }
  },

  formFieldCompletion: (formName, fieldName, fieldValue) => {
    const label = fieldValue ? `${fieldName}_${fieldValue}` : fieldName;
    pushToDataLayer("field_completion", formName, label);
  },

  formInputError: (formName, fieldName, fieldError) => {
    pushToDataLayer(
      "validation_errors",
      formName,
      `${fieldName}_${fieldError}`,
    );
  },

  // Format the value of the field pushed to the data layer based on the type of the field
  // Values typed into text fields should not be sent to the data layer, so return an empty string
  getFormFieldValue: (field) => {
    switch (field.tagName) {
      case "SELECT":
        return field.value;
      case "INPUT":
        switch (field.type) {
          case "checkbox":
            return field.checked ? "yes" : "no";
          case "radio":
            return field.checked ? field.value : "";
          case "text":
          case "email":
          case "password":
          case "tel":
          case "number":
          default:
            return "";
        }
      default:
        return "";
    }
  },

  useFormTracking: (formName, formRef = null) => {
    const [completedFields, setCompletedFields] = useState({});

    const handleFieldCompletionTracking = useCallback(
      (event, value) => {
        const fieldValue = value ? value : GTM.getFormFieldValue(event.target);
        setCompletedFields((prev) => ({
          ...prev,
          [event.target.name]: fieldValue,
        }));
        GTM.formFieldCompletion(formName, event.target.name, fieldValue);
      },
      [formName],
    );

    const handleFormSubmissionTracking = () => {
      GTM.formSubmission(formName, completedFields);
    };

    const handleFormInputErrorTracking = (fieldName, fieldError) => {
      GTM.formInputError(formName, fieldName, fieldError);
    };

    useEffect(() => {
      if (formRef?.current) {
        const currentForm = formRef.current;
        currentForm.addEventListener("change", handleFieldCompletionTracking);
        return () => {
          currentForm.removeEventListener(
            "change",
            handleFieldCompletionTracking,
          );
        };
      }
    }, [formRef, handleFieldCompletionTracking]);

    return {
      handleFieldCompletionTracking,
      handleFormSubmissionTracking,
      handleFormInputErrorTracking,
      completedFields,
      setCompletedFields,
    };
  },

  /*** Form tags END ***/

  expandableContent: (isExpanded, title) => {
    const titleString = extractTextFromElement(title);
    pushToDataLayer(
      "expansions",
      isExpanded ? "Expand" : "Collapse",
      titleString,
    );
  },

  interstitial: (interstitialVisible, interstitialName) => {
    const event = interstitialVisible
      ? "interstitial_load"
      : "interstitial_dismiss";
    pushToDataLayer(event, null, interstitialName);
  },

  isiInteraction: (isExpanded) => {
    pushToDataLayer("isi", null, isExpanded ? "Expand" : "Collapse");
  },

  tabClick: (event, tabName = "", pageLoad = false) => {
    const label = tabName || event.target?.textContent?.trim() || "";
    pushToDataLayer(
      "tab_click",
      null,
      `${pageLoad ? "Pageload_" : "Click_"}${label}`,
    );
  },

  carouselInteraction: (currentSlideTitle, selectedSlideTitle) => {
    const currentSlideTitleString = extractTextFromElement(currentSlideTitle);
    const selectedSlideTitleString = extractTextFromElement(selectedSlideTitle);
    pushToDataLayer(
      "carousels",
      null,
      `${currentSlideTitleString}_${selectedSlideTitleString}`,
    );
  },

  /*** Custom hook for long-scroll pages ***/
  useInViewportTracking: (ref, crossPoint = 0.5) => {
    const handleInView = () => pushToDataLayer("custom_page_view");
    return useElementCrossing(ref, {
      onCrossUp: handleInView,
      onCrossDown: handleInView,
      crossPoint,
    });
  },

  reset: () => {
    GTM.distanceScrolled = -1;
    GTM.scrollThresholds.forEach((threshold) => {
      threshold.triggered = false;
    });
    window.removeEventListener("scroll", GTM.scrollTracking);
  },

  useGTMTracking: () => {
    useEffect(() => {
      GTM.init();

      return () => {
        GTM.reset(); // Required for accurate scroll depth tracking
      };
    }, []);
  },
};

export default GTM;
